import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  createContext,
  useContext,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import "./Toast.css";

// Toast Context
const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

// Toast Provider Component
// Maximum number of toasts visible at once to prevent UI flooding
const MAX_VISIBLE_TOASTS = 5;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef({});

  const removeToast = useCallback((id) => {
    // Clear any associated timeout
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message, type = "info", duration = 5000) => {
      let id = null;
      setToasts((prev) => {
        // Deduplicate: skip if an identical message+type is already showing
        if (prev.some((t) => t.message === message && t.type === type)) {
          return prev;
        }

        // Cap visible toasts to prevent UI flooding — evict oldest
        let base = prev;
        if (prev.length >= MAX_VISIBLE_TOASTS) {
          const evicted = prev.slice(0, prev.length - MAX_VISIBLE_TOASTS + 1);
          evicted.forEach((t) => {
            if (timeoutsRef.current[t.id]) {
              clearTimeout(timeoutsRef.current[t.id]);
              delete timeoutsRef.current[t.id];
            }
          });
          base = prev.slice(prev.length - MAX_VISIBLE_TOASTS + 1);
        }

        // Generate unique ID with better collision resistance
        id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return [...base, { id, message, type, duration }];
      });

      // Schedule auto-dismiss (id may be null if deduplicated)
      if (duration > 0 && id) {
        const timeoutId = setTimeout(() => {
          removeToast(id);
        }, duration);

        // Store timeout ID for cleanup
        timeoutsRef.current[id] = timeoutId;
      }

      return id;
    },
    [removeToast],
  );

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutsRef.current = {};
    };
  }, []);

  const toast = useMemo(
    () => ({
      success: (message, duration) => addToast(message, "success", duration),
      error: (message, duration) => addToast(message, "error", duration),
      warning: (message, duration) => addToast(message, "warning", duration),
      info: (message, duration) => addToast(message, "info", duration),
    }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Toast Container Component
const ToastContainer = ({ toasts, onClose }) => {
  const containerRef = useRef(null);

  // Handle Escape key to dismiss all toasts
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && toasts.length > 0) {
        // Close the most recent toast
        onClose(toasts[toasts.length - 1].id);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [toasts, onClose]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="toast-container"
      ref={containerRef}
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => onClose(toast.id)} />
      ))}
    </div>
  );
};

const toastShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "error", "warning", "info"]),
  duration: PropTypes.number,
});

ToastContainer.propTypes = {
  toasts: PropTypes.arrayOf(toastShape).isRequired,
  onClose: PropTypes.func.isRequired,
};

// Individual Toast Component
const Toast = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    // Auto-dismiss animation before removal
    // Only start exit animation if duration is greater than 300ms
    if (toast.duration > 300) {
      exitTimerRef.current = setTimeout(() => {
        setIsExiting(true);
      }, toast.duration - 300); // Start exit animation 300ms before removal
    }

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
      }
    };
  }, [toast.duration]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClose();
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
          </svg>
        );
      case "error":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0zm9.78-2.22a.75.75 0 0 0-1.06-1.06L8 6.94 5.78 4.72a.75.75 0 0 0-1.06 1.06L6.94 8l-2.22 2.22a.75.75 0 1 0 1.06 1.06L8 9.06l2.22 2.22a.75.75 0 1 0 1.06-1.06L9.06 8l2.22-2.22z" />
          </svg>
        );
      case "warning":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
          </svg>
        );
      default:
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm9 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM6.92 5.5c.036-.146.087-.29.154-.427.066-.136.16-.26.28-.372C7.49 4.56 7.673 4.5 7.875 4.5c.18 0 .343.042.488.126.145.083.259.2.342.35.083.15.125.319.125.507 0 .186-.04.354-.118.505a1.47 1.47 0 0 1-.314.397.89.89 0 0 0-.23.288c-.054.11-.08.229-.08.357h1.125c0-.235.06-.454.18-.656.12-.202.283-.376.488-.523a2.32 2.32 0 0 0 .408-.4c.11-.138.196-.29.257-.453A1.78 1.78 0 0 0 10.75 4c0-.365-.094-.699-.28-.999a2.047 2.047 0 0 0-.773-.706C9.328 2.098 8.89 2 8.375 2c-.606 0-1.14.13-1.601.39a2.31 2.31 0 0 0-1.094 1.61z" />
          </svg>
        );
    }
  };

  return (
    <div
      className={`toast toast-${toast.type} ${isExiting ? "toast-exit" : ""}`}
      role="alert"
      aria-live={toast.type === "error" ? "assertive" : "polite"}
    >
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-message">{toast.message}</div>
      <button
        className="toast-close"
        onClick={onClose}
        onKeyDown={handleKeyDown}
        aria-label="Close notification"
        tabIndex={0}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </button>
    </div>
  );
};

Toast.propTypes = {
  toast: toastShape.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Toast;
